'use client';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { ElementType, ReactNode } from 'react';
import React, { useLayoutEffect, useRef } from 'react';

import { shouldDisableAnimations } from './utils';

gsap.registerPlugin(ScrollTrigger);

export type FadeUpOnScrollProps = {
  /** Root element to animate (default `div`). Use e.g. `KibCarouselItem` to animate the slide `li` directly. */
  as?: ElementType;
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
};

export const FadeUpOnScroll = ({
  as: Component = 'div',
  children,
  className,
  delay = 0,
  duration = 1,
  y = 80,
}: FadeUpOnScrollProps) => {
  const ref = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (shouldDisableAnimations()) return;

    const element = ref.current;
    if (!element) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        element,
        {
          autoAlpha: 0,
          y,
        },
        {
          autoAlpha: 1,
          y: 0,
          duration,
          delay,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 90%',
          },
        },
      );
    }, ref);

    return () => ctx.revert();
  }, [delay, duration, y]);

  return React.createElement(Component, { ref, className }, children);
};
